import type { SceneMetrics } from '../scene/BedroomScene'

const requiredElement = <T extends HTMLElement>(selector: string): T => {
  const element = document.querySelector<T>(selector)
  if (!element) throw new Error(`Missing required UI element: ${selector}`)
  return element
}

export class AppUI {
  private readonly app = requiredElement<HTMLElement>('#app')
  private readonly overlay = requiredElement<HTMLElement>('#loading-overlay')
  private readonly progress = requiredElement<HTMLElement>('#loading-progress')
  private readonly track = requiredElement<HTMLElement>('#loading-track')
  private readonly label = requiredElement<HTMLElement>('#loading-label')
  private readonly detail = requiredElement<HTMLElement>('#loading-detail')
  private readonly info = requiredElement<HTMLElement>('#scene-info')
  private readonly infoText = requiredElement<HTMLElement>('#scene-info-text')
  private readonly errorPanel = requiredElement<HTMLElement>('#error-panel')
  private readonly errorMessage = requiredElement<HTMLElement>('#error-message')
  private readonly startButton = requiredElement<HTMLButtonElement>('#start-button')
  private infoMessage = 'Preparing the room'
  private infoState = ''
  private feedbackTimer: number | null = null
  private startResolve: (() => void) | null = null

  public setAuthoringMode(enabled: boolean): void {
    this.app.dataset.authoringMode = enabled ? 'true' : 'false'
    this.info.setAttribute('aria-hidden', enabled ? 'false' : 'true')
  }

  private setInfo(message: string, state: string): void {
    this.infoMessage = message
    this.infoState = state
    this.info.dataset.state = state
    this.infoText.textContent = message
  }

  private showTemporaryInfo(message: string): void {
    if (this.feedbackTimer !== null) window.clearTimeout(this.feedbackTimer)
    this.info.dataset.state = 'ready'
    this.infoText.textContent = message
    this.feedbackTimer = window.setTimeout(() => {
      this.info.dataset.state = this.infoState
      this.infoText.textContent = this.infoMessage
      this.feedbackTimer = null
    }, 2200)
  }

  public setLoading(loaded: number, total: number, _url: string): void {
    this.app.dataset.gameState = 'loading'
    this.overlay.dataset.state = 'loading'
    const percentage = total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 12
    this.progress.style.width = `${Math.max(6, percentage)}%`
    this.track.setAttribute('aria-valuenow', String(percentage))
    this.label.textContent = 'Loading assets'
    this.detail.textContent = `Preparing all story scenes · ${percentage}%`
  }

  public waitForStart(metrics: SceneMetrics): Promise<void> {
    if (this.startResolve) throw new Error('The start prompt is already waiting for input.')
    this.setInfo(`${metrics.meshes} opening-scene meshes · all scenes downloaded`, 'ready')
    this.progress.style.width = '100%'
    this.track.setAttribute('aria-valuenow', '100')
    this.label.textContent = 'Ready to begin'
    this.detail.textContent = 'Press START for audio and gameplay'
    this.app.dataset.gameState = 'ready'
    this.overlay.dataset.state = 'ready'
    this.startButton.hidden = false
    this.startButton.disabled = false

    return new Promise((resolve) => {
      this.startResolve = resolve
      this.startButton.addEventListener('click', this.handleStartClick, { once: true })
    })
  }

  private readonly handleStartClick = (): void => {
    this.startButton.disabled = true
    this.overlay.dataset.state = 'starting'
    this.app.dataset.gameState = 'starting'
    this.label.textContent = 'Starting the story'
    this.detail.textContent = 'Audio on · entering gameplay'
    const resolve = this.startResolve
    this.startResolve = null
    resolve?.()
  }

  public hideStartOverlay(): void {
    this.overlay.classList.add('is-hidden')
  }

  public setGamePhase(state: string, message: string): void {
    this.app.dataset.gameState = state
    this.setInfo(message, 'ready')
  }

  public setSceneUiHidden(hidden: boolean): void {
    this.app.dataset.sceneUiHidden = hidden ? 'true' : 'false'
  }

  public showContextLost(): void {
    this.setInfo('Graphics context paused', 'warning')
  }

  public showContextRestored(): void {
    this.setInfo('Graphics context restored', 'ready')
  }

  public showCameraPoseCaptured(copied: boolean): void {
    this.showTemporaryInfo(
      copied ? 'Camera pose copied · console logged' : 'Camera pose logged · copy blocked',
    )
  }

  public showError(error: unknown): void {
    const message = error instanceof Error ? error.message : String(error)
    this.overlay.classList.add('is-hidden')
    this.errorMessage.textContent = message
    this.errorPanel.hidden = false
    this.app.dataset.gameState = 'error'
    this.setInfo('Scene unavailable', 'error')
  }

  public dispose(): void {
    this.startButton.removeEventListener('click', this.handleStartClick)
    if (this.feedbackTimer !== null) window.clearTimeout(this.feedbackTimer)
    this.feedbackTimer = null
    const resolve = this.startResolve
    this.startResolve = null
    resolve?.()
  }
}
